import Cookies from 'js-cookie';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { storeAuthenticationObject } from '../../helpers/storage/storage';
import { ActivationResult, LoginResult, ResetPasswordResult, SetPasswordResult } from './auth-client.class';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  ActivationRequest,
  ActivationResponse,
  DomainCheckResponse,
  LoginRequest,
  LoginResponse,
  OTPResponse,
  ResetPasswordResponse,
  SetPasswordResponse,
  SignUpPayload,
} from './auth-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/auth/';

/**
 * Logs a user in. Requires a email / password in the result.
 *
 * @param {LoginRequest} body - The login request body
 * @returns {Promise<LoginResponse>} A promise that resolves with the login result
 */
function login(body: LoginRequest): Promise<LoginResponse> {
  const apiURL = baseURL + 'jwt/create/';
  const postBody = { email: body.email.toLowerCase(), password: body.password };
  return axiosInstance
    .post<LoginResponse>(apiURL, postBody)
    .then((response) => {
      storeAuthenticationObject(response.data);
      return new LoginResult(response.data);
    })
    .catch((error) => {
      const errorMessage = error.response.data;
      return errorMessage;
    });
}

/**
 * Checks the domain for a user / organization
 *
 * @param {string} domain - The domain to check
 * @returns {Promise<DomainCheckResponse>} A promise that resolves with the domain check response
 */
function checkDomain(domain: string): Promise<DomainCheckResponse> {
  const apiURL = appConfig.VITE_APP_API_URL + `/api/openid/check_domain/?domain=${domain}`;
  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => error.response.data);
}

/**
 * Registers a new user.
 *
 * @param {SignUpPayload} payload - The signup details including email and password.
 * @returns {Promise<SignUpPayload>} A promise that resolves with the signup payload if successful.
 *          Rejects with an error object if the request fails.
 */
function signup(payload: SignUpPayload): Promise<SignUpPayload> {
  const apiURL = baseURL + 'users/';
  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Sets password for given email and code, with new password
 *
 * @param {string} email - email address to resend code to
 * @param {string} code - verification code
 * @param {string} newPassword - new password to set for user
 * @returns {Promise<SetPasswordResult>} Promise containing success/error message
 */
function setPassword(email: string, code: string, newPassword: string): Promise<SetPasswordResult> {
  const apiURL = baseURL + 'users/reset_password_confirm/';
  const postBody = { uid: email.toLowerCase(), token: code, new_password: newPassword, re_new_password: newPassword };
  return axiosInstance
    .post<SetPasswordResponse>(apiURL, postBody)
    .then((response) => {
      return new SetPasswordResult(response.status);
    })
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Sends password reset code to provided email address
 *
 * @param {string} email - email address to resend code to
 * @returns {Promise<ResetPasswordResult>} Promise containing success/error message
 */
function resetPassword(email: string): Promise<ResetPasswordResult> {
  const apiURL = baseURL + 'users/reset_password/';
  const postBody = { email: email.toLowerCase() };
  return axiosInstance
    .post<ResetPasswordResponse>(apiURL, postBody, {
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken'),
      },
    })
    .then((response) => {
      return new ResetPasswordResult(response.status);
    })
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Verifies a one-time password (OTP) for a given email
 *
 * @param {string} email - Email address associated with the OTP
 * @param {string} otp - One-time password code to verify
 * @returns {Promise<OTPResponse>} Promise containing success/error message
 */
function verifyOTP(email: string, otp: string): Promise<OTPResponse> {
  const apiURL = `${appConfig.VITE_APP_API_URL}/api/otp/verify`;
  const params = { email, otp };

  return axiosInstance
    .get(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Resends a one-time password (OTP) to the specified email address
 *
 * @param {string} email - Email address to resend the OTP to
 * @returns {Promise<OTPResponse>} Promise containing success/error message
 */
function resendOTP(email: string): Promise<OTPResponse> {
  const apiURL = `${appConfig.VITE_APP_API_URL}/api/otp/resend`;
  const params = { email };

  return axiosInstance
    .get(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Activates a user account using uid and token from activation email
 *
 * @param {string} uid - Base64 encoded user ID from activation email
 * @param {string} token - Activation token from email
 * @returns {Promise<ActivationResult>} Promise containing success/error message
 */
function activate(uid: string, token: string): Promise<ActivationResult> {
  const apiURL = baseURL + 'users/activation/';
  const postBody: ActivationRequest = { uid, token };
  return axiosInstance
    .post<ActivationResponse>(apiURL, postBody)
    .then((response) => new ActivationResult(response.status))
    .catch((error) => Promise.reject({ error: error.response.data }));
}

export { activate, checkDomain, login, resendOTP, resetPassword, setPassword, signup, verifyOTP };
